import React from 'react';
import { Box, Container, Typography, Paper, useTheme, useMediaQuery } from '@mui/material';
import { motion } from 'framer-motion';

const About = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.6 },
    },
  };

  return (
    <Box
      sx={{
        py: { xs: 4, md: 6 },
        background: 'linear-gradient(180deg, #f5f5f5 0%, #ffffff 100%)',
      }}
    >
      <Container maxWidth="lg">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <Typography
            variant={isMobile ? 'h4' : 'h3'}
            sx={{
              textAlign: 'center',
              mb: { xs: 3, sm: 3.5, md: 4 },
              fontWeight: 700,
              background: 'linear-gradient(90deg, #0968E5, #091970)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              fontSize: { xs: '1.75rem', sm: '2rem', md: '2.5rem', lg: '3rem' },
            }}
          >
            About Me
          </Typography>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 3, md: 5 },
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(249, 250, 251, 0.95) 100%)',
              border: '2px solid rgba(9, 104, 229, 0.15)',
              boxShadow: '0 4px 20px rgba(9, 104, 229, 0.1)',
              transition: 'all 0.3s ease',
              position: 'relative',
              overflow: 'hidden',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '4px',
                background: 'linear-gradient(90deg, #0968E5, #091970)',
              },
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 12px 50px rgba(9, 104, 229, 0.2)',
                border: '2px solid rgba(9, 104, 229, 0.3)',
              },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 2, md: 2.5 },
              }}
            >
              <motion.div variants={itemVariants}>
                <Typography
                  variant={isMobile ? 'body2' : 'body1'}
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1.05rem', lg: '1.2rem' },
                    lineHeight: { xs: 1.6, sm: 1.7, md: 1.75 },
                    color: '#2d3748',
                    textAlign: { xs: 'left', sm: 'justify' },
                  }}
                >
                  With over <Box component="span" sx={{ fontWeight: 700, color: '#0968E5' }}>17 years</Box> of
                  comprehensive experience, I am a seasoned sales and marketing professional who has mastered the art of
                  transforming business challenges into growth opportunities. My journey spans across{' '}
                  <Box component="span" sx={{ fontWeight: 700, color: '#0968E5' }}>Dubai, PAN Africa, and South Asian countries</Box>,
                  where I've consistently driven revenue growth and market expansion in highly competitive environments.
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography
                  variant={isMobile ? 'body2' : 'body1'}
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1.05rem', lg: '1.2rem' },
                    lineHeight: { xs: 1.6, sm: 1.7, md: 1.75 },
                    color: '#2d3748',
                    textAlign: { xs: 'left', sm: 'justify' },
                  }}
                >
                  My expertise lies in crafting strategic sales and marketing plans that don't just meet targets—they{' '}
                  <Box component="span" sx={{ fontWeight: 700, color: '#0968E5' }}>exceed expectations</Box>. From
                  launching innovative products to managing key accounts, I've built a reputation for delivering
                  measurable results that drive sustainable business growth. Whether it's orchestrating international trade
                  exhibitions or optimizing operational efficiency, I bring a data-driven approach combined with creative
                  problem-solving to every challenge.
                </Typography>
              </motion.div>

              <motion.div variants={itemVariants}>
                <Typography
                  variant={isMobile ? 'body2' : 'body1'}
                  sx={{
                    fontSize: { xs: '0.875rem', sm: '0.95rem', md: '1.05rem', lg: '1.2rem' },
                    lineHeight: { xs: 1.6, sm: 1.7, md: 1.75 },
                    color: '#2d3748',
                    textAlign: { xs: 'left', sm: 'justify' },
                  }}
                >
                  From 2010 to 2015, forged excellence as a seasoned sales professional in the steel industry, mastering
                  pan-India sales with deep expertise in the eastern region. Built an extensive network and specialized
                  knowledge in{' '}
                  <Box component="span" sx={{ fontWeight: 700, color: '#0968E5' }}>
                    annealing sheets, GC sheets, CR and HR sheets, and MS pipes
                  </Box>
                  . Passionate about driving business growth, leveraging strategic connections and industry insights to
                  identify opportunities and expand market reach—delivering exceptional results and contributing to
                  organizational success.
                </Typography>
              </motion.div>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default About;

